import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiDollarSign, FiCreditCard, FiCheck } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import BottomTabMenu from '../../components/UI/BottomTabMenu';
import { clientesService, facturasService } from '../../services';

// Formatea una fecha 'YYYY-MM-DD' como local (evita el desfase de un día por UTC)
const formatFecha = (dateStr) => {
  if (!dateStr) return 'N/A';
  const parts = String(dateStr).split('T')[0].split('-');
  if (parts.length === 3) {
    return new Date(+parts[0], +parts[1] - 1, +parts[2]).toLocaleDateString('es-CO');
  }
  return new Date(dateStr).toLocaleDateString('es-CO');
};

// Fecha de hoy en formato YYYY-MM-DD (hora local)
const hoyISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const ClientePagar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const facturaId = searchParams.get('factura');

  const [facturasPendientes, setFacturasPendientes] = useState([]);
  const [selectedFacturas, setSelectedFacturas] = useState([]);
  const [metodoPago, setMetodoPago] = useState('');
  const [referencia, setReferencia] = useState('');
  const [monto, setMonto] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFacturasPendientes();
  }, [id]);

  useEffect(() => {
    if (facturaId && facturasPendientes.length > 0) {
      const facturaToSelect = facturasPendientes.find(f => f.id === parseInt(facturaId));
      if (facturaToSelect) {
        setSelectedFacturas([facturaToSelect.id]);
      }
    }
  }, [facturaId, facturasPendientes]);

  const loadFacturasPendientes = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Obtener facturas del cliente (traemos todas y filtramos por saldo pendiente,
      // para incluir tanto las NO pagadas como las PARCIALES)
      const result = await clientesService.getFacturasCliente(id, {
        skip: 0,
        limit: 100
      });

      if (result.success) {
        // Manejar diferentes estructuras de respuesta
        const dataArray = Array.isArray(result.data)
          ? result.data
          : (result.data?.items || result.data?.invoices || []);

        const facturas = dataArray
          // Solo facturas con saldo pendiente y que no estén anuladas/canceladas
          .filter(f => {
            const saldo = f.balance != null
              ? f.balance
              : (f.total || f.total_amount || 0) - (f.paid_amount || 0);
            const estado = f.status || '';
            return saldo > 0 && estado !== 'cancelled' && estado !== 'void' && estado !== 'voided';
          })
          .map(f => ({
            id: f.id,
            numeroFactura: f.number || f.invoice_number || `INV-${f.id}`,
            fecha: f.invoice_date || f.created_at,
            fechaVencimiento: f.due_date,
            total: f.total || f.total_amount || 0,
            saldoPendiente: f.balance != null
              ? f.balance
              : (f.total || f.total_amount || 0) - (f.paid_amount || 0),
            estado: f.payment_status || 'unpaid'
          }));

        setFacturasPendientes(facturas);
      } else {
        setError(result.message || 'Error al cargar las facturas');
      }
    } catch (error) {
      console.error('Error cargando facturas:', error);
      setError('Error de conexión al cargar las facturas');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleFactura = (facturaId) => {
    setSelectedFacturas(prev =>
      prev.includes(facturaId)
        ? prev.filter(id => id !== facturaId)
        : [...prev, facturaId]
    );
  };

  const handleProcesarPago = async () => {
    if (selectedFacturas.length === 0 || !metodoPago) {
      alert('Por favor selecciona al menos una factura y un método de pago');
      return;
    }

    const seleccionadas = facturasPendientes.filter(f => selectedFacturas.includes(f.id));

    // El monto parcial solo aplica cuando se paga UNA sola factura
    const montoParcial = parseFloat(monto);
    const usarMontoParcial = seleccionadas.length === 1 && !isNaN(montoParcial) && montoParcial > 0;

    if (usarMontoParcial && montoParcial > seleccionadas[0].saldoPendiente) {
      alert('El monto ingresado supera el saldo pendiente de la factura.');
      return;
    }

    if (!usarMontoParcial && !isNaN(montoParcial) && montoParcial > 0 && seleccionadas.length > 1) {
      alert('El monto parcial solo se puede usar al pagar una sola factura. Selecciona una factura o deja el monto en blanco para pagar el saldo completo de cada una.');
      return;
    }

    setIsSaving(true);
    setError(null);

    const fecha = hoyISO();
    const fallidas = [];

    try {
      // Registrar el pago factura por factura contra el backend real
      for (const factura of seleccionadas) {
        const amount = usarMontoParcial ? montoParcial : factura.saldoPendiente;

        const result = await facturasService.markFacturaAsPaid(factura.id, {
          amount,
          payment_method: metodoPago,
          payment_date: fecha,
          reference_number: referencia || null
        });

        if (!result.success) {
          fallidas.push(`${factura.numeroFactura}: ${result.message || 'error'}`);
        }
      }

      if (fallidas.length > 0) {
        setError(`No se pudieron registrar algunos pagos:\n${fallidas.join('\n')}`);
      } else {
        alert('Pago(s) registrado(s) exitosamente');
        navigate(`/system/clientes/${id}/pagos`);
      }
    } catch (error) {
      console.error('Error procesando pago:', error);
      setError('Error al procesar el pago');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingGrid count={3} message="Cargando facturas..." />;
  }

  const totalSeleccionado = facturasPendientes
    .filter(f => selectedFacturas.includes(f.id))
    .reduce((sum, f) => sum + (f.saldoPendiente || f.total), 0);

  return (
    <div className="cliente-pagar-page">
      {/* Header */}
      <div className="page-header-simple">
        <button onClick={() => navigate(`/system/clientes/${id}/estado-cuenta`)} className="btn btn--ghost">
          <FiArrowLeft /> Volver
        </button>
        <h1 className="page-title">PAGAR FACTURAS</h1>
      </div>

      {/* Error message */}
      {error && (
        <div className="alert alert--danger" style={{ marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {/* Facturas pendientes */}
      <div className="facturas-section">
        <h3 className="section-title">Selecciona las facturas a pagar</h3>

        {facturasPendientes.length === 0 ? (
          <div className="empty-state">
            <p>No hay facturas pendientes para este cliente</p>
          </div>
        ) : (
          <div className="facturas-seleccion">
            {facturasPendientes.map((factura) => (
              <div
                key={factura.id}
                className={`factura-seleccion-item ${selectedFacturas.includes(factura.id) ? 'selected' : ''}`}
                onClick={() => handleToggleFactura(factura.id)}
              >
                <div className="checkbox-wrapper">
                  <input
                    type="checkbox"
                    checked={selectedFacturas.includes(factura.id)}
                    onChange={() => handleToggleFactura(factura.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>

                <div className="factura-info">
                  <div className="factura-numero">{factura.numeroFactura}</div>
                  <div className="factura-fecha">
                    Fecha: {formatFecha(factura.fecha)}
                  </div>
                  {factura.fechaVencimiento && (
                    <div className="factura-vencimiento" style={{ fontSize: '0.85em', color: '#6b7280' }}>
                      Vence: {formatFecha(factura.fechaVencimiento)}
                    </div>
                  )}
                </div>

                <div className="factura-monto">
                  <div style={{ fontWeight: 'bold' }}>
                    ${(factura.saldoPendiente || factura.total).toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                  </div>
                  {factura.saldoPendiente !== factura.total && (
                    <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                      Total: ${factura.total.toLocaleString('es-CO', { minimumFractionDigits: 2 })}
                    </div>
                  )}
                </div>

                {selectedFacturas.includes(factura.id) && (
                  <FiCheck className="check-icon" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Formulario de pago */}
      <div className="pago-form-section">
        <h3 className="section-title">Información del pago</h3>

        <div className="form-group">
          <label className="form-label">Método de Pago</label>
          <select
            className="form-input"
            value={metodoPago}
            onChange={(e) => setMetodoPago(e.target.value)}
          >
            <option value="">Selecciona un método</option>
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia</option>
            <option value="cheque">Cheque</option>
            <option value="tarjeta">Tarjeta</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Referencia / Número de Transacción</label>
          <input
            type="text"
            className="form-input"
            value={referencia}
            onChange={(e) => setReferencia(e.target.value)}
            placeholder="Ej: CHK-1234, TRF-5678"
          />
        </div>

        <div className="form-group">
          <label className="form-label">Monto (opcional para pago parcial)</label>
          <input
            type="number"
            className="form-input"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            placeholder={`Total: $${totalSeleccionado.toLocaleString()}`}
            step="0.01"
          />
        </div>
      </div>

      {/* Resumen */}
      <div className="pago-resumen">
        <div className="resumen-item">
          <span>Facturas seleccionadas:</span>
          <strong>{selectedFacturas.length}</strong>
        </div>
        <div className="resumen-item total">
          <span>Total a pagar:</span>
          <strong className="total-monto">
            ${(monto || totalSeleccionado).toLocaleString()}
          </strong>
        </div>
      </div>

      {/* Botones */}
      <div className="form-actions">
        <button
          onClick={() => navigate(`/system/clientes/${id}/estado-cuenta`)}
          className="btn btn--outline"
          disabled={isSaving}
        >
          Cancelar
        </button>
        <button
          onClick={handleProcesarPago}
          className="btn btn--primary"
          disabled={isSaving || selectedFacturas.length === 0}
        >
          {isSaving ? (
            <>
              <div className="loading-spinner-single" style={{ width: '16px', height: '16px' }}></div>
              Procesando...
            </>
          ) : (
            <>
              <FiDollarSign /> Procesar Pago
            </>
          )}
        </button>
      </div>

      {/* Menú inferior */}
      <BottomTabMenu baseRoute={`/system/clientes/${id}`} currentTab="pagar" />
    </div>
  );
};

export default ClientePagar;
